import React from 'react';
import { Circle } from 'lucide-react';
import { cn } from '../utils/cn';
import { formatDate } from '../utils/format';
import { TONE_CLASSES, type Tone } from '../utils/statusTone';

export interface ILMSTimelineItem {
  id: string;
  date: string;
  title: string;
  description?: string;
  user?: string;
  icon?: React.ReactNode;
  tone?: Tone;
  meta?: React.ReactNode;
}

export interface ILMSTimelineProps {
  items: ILMSTimelineItem[];
  className?: string;
}

export function ILMSTimeline({ items, className }: ILMSTimelineProps) {
  if (items.length === 0) {
    return <p className="text-secondary text-ink-500 py-6 text-center">No history recorded yet.</p>;
  }
  return (
    <ol className={cn('relative border-l-2 border-ink-300/20 ml-3', className)}>
      {items.map((item) => {
        const tone = item.tone ?? 'neutral';
        const classes = TONE_CLASSES[tone];
        return (
          <li key={item.id} className="mb-6 last:mb-0 ml-5 relative">
            <span
              className={cn(
                'absolute -left-[1.65rem] top-0.5 flex h-6 w-6 items-center justify-center rounded-full border-2 border-white',
                classes.bg,
                classes.text
              )}
            >
              {item.icon ?? <Circle className="h-3 w-3" fill="currentColor" />}
            </span>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <p className="text-card-title">{item.title}</p>
              <span className="text-helper-text text-ink-400">{formatDate(item.date)}</span>
            </div>
            {item.description && <p className="text-secondary text-ink-700 mt-0.5">{item.description}</p>}
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              {item.user && <span className="text-helper-text text-ink-400">By {item.user}</span>}
              {item.meta}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
